import { API_BASE_URL } from "../../consts/apiRoutes.consts"
import { route } from "../../routers/routes"
import * as requester from "../base/api.service"
import { getRequest, postRequest } from "../../utils/request.utils"

/**
 * @param {string} chatId
 * @returns {string}
 */
export function chatMessagesUrl(chatId) {
  return `${API_BASE_URL}${route(
    `/estimation-api/agent/quick/v3/chats/${chatId}/messages`
  )}`
}

/**
 * @returns {Promise<
 *   | { success: true; chats: { id: string; title: string; user: number }[] }
 *   | { success: false }
 * >}
 */
export async function listChats() {
  const res = await getRequest(route("/estimation-api/agent/quick/v3/chats"))
  if (!res.success) return { success: false }
  const raw = res.body?.data
  if (!Array.isArray(raw)) return { success: false }
  const chats = raw.map((row) => {
    const id = row?.id
    const title = row?.title
    const user = row?.user
    if (typeof id !== "string" || typeof user !== "number")
      return null
    return {
      id,
      title: typeof title === "string" ? title : "",
      user,
    }
  })
  if (chats.some((c) => c == null)) return { success: false }
  /** @type {{ id: string; title: string; user: number }[]} */
  const chatList = /** @type {{ id: string; title: string; user: number }[]} */ (
    chats.filter((c) => c != null)
  )
  return { success: true, chats: chatList }
}

/**
 * @returns {Promise<
 *   | {
 *       success: true;
 *       data: {
 *         id: string;
 *         title: string;
 *         user: number;
 *         messages: unknown;
 *         context: unknown;
 *         createdAt: number;
 *       };
 *     }
 *   | { success: false }
 * >}
 */
export async function getChatById(chatId) {
  if (typeof chatId !== "string" || !chatId.trim()) return { success: false }
  const path = route(
    `/estimation-api/agent/quick/v3/chats/${encodeURIComponent(chatId)}`
  )
  const url = `${API_BASE_URL}${path}`
  const res = await requester.get(url, undefined, {})
  if (!res.ok) return { success: false }
  try {
    const body = await res.json()
    const data = body?.data
    if (!data || typeof data !== "object") return { success: false }
    const id = data.id
    if (typeof id !== "string") return { success: false }
    return {
      success: true,
      data: {
        id,
        title: typeof data.title === "string" ? data.title : "",
        user: typeof data.user === "number" ? data.user : 0,
        messages: data.messages,
        context: data.context,
        createdAt:
          typeof data.createdAt === "number" ? data.createdAt : 0,
      },
    }
  } catch {
    return { success: false }
  }
}

/**
 * @returns {Promise<{ success: true; chatId: string } | { success: false }>}
 */
export async function createChat() {
  const res = await postRequest(
    route("/estimation-api/agent/quick/v3/chats"),
    {}
  )
  if (!res.success) return { success: false }
  const chatId = res.body?.data?.chatId
  if (typeof chatId !== "string" || !chatId) return { success: false }
  return { success: true, chatId }
}

/**
 * @param {string} chatId
 * @param {string} content
 * @returns {Promise<{ success: true; title: string; updated: boolean } | { success: false }>}
 */
export async function generateChatTitle(chatId, content) {
  const res = await postRequest(
    route(`/estimation-api/agent/quick/v3/chats/${chatId}/generate-title`),
    { content }
  )
  if (!res.success) return { success: false }
  const title = res.body?.data?.title
  const updated = res.body?.data?.updated
  if (typeof title !== "string") return { success: false }
  return { success: true, title, updated: Boolean(updated) }
}

/**
 * @param {string} chatId
 * @param {File} file
 * @param {Pick<RequestInit, "signal">} [fetchOptions]
 * @returns {Promise<
 *   | { success: true; url: string; storagePath?: string }
 *   | { success: false; error: string }
 * >}
 */
/**
 * @param {string} chatId
 * @param {string} title
 * @returns {Promise<{ success: true; title: string } | { success: false }>}
 */
export async function updateChatTitle(chatId, title) {
  if (typeof chatId !== "string" || !chatId.trim()) return { success: false }
  const raw = typeof title === "string" ? title.trim() : ""
  if (raw.length < 1 || raw.length > 25) return { success: false }
  const path = route(
    `/estimation-api/agent/quick/v3/chats/${encodeURIComponent(chatId)}`
  )
  const url = `${API_BASE_URL}${path}`
  const res = await requester.patch(url, { title: raw }, {})
  if (!res.ok) return { success: false }
  try {
    const body = await res.json()
    const t = body?.data?.title
    if (typeof t !== "string") return { success: false }
    return { success: true, title: t }
  } catch {
    return { success: false }
  }
}

/**
 * @param {string} chatId
 * @returns {Promise<{ success: true } | { success: false }>}
 */
export async function deleteChat(chatId) {
  if (typeof chatId !== "string" || !chatId.trim()) return { success: false }
  const path = route(
    `/estimation-api/agent/quick/v3/chats/${encodeURIComponent(chatId)}`
  )
  const url = `${API_BASE_URL}${path}`
  const res = await requester.del(url, undefined, {})
  if (res.status === 204 || res.ok) return { success: true }
  return { success: false }
}

export async function uploadChatAttachment(chatId, file, fetchOptions = {}) {
  if (typeof chatId !== "string" || !chatId.trim()) {
    return { success: false, error: "Chat no disponible" }
  }
  const path = route(
    `/estimation-api/agent/quick/v3/chats/${encodeURIComponent(chatId)}/attachments`
  )
  const url = `${API_BASE_URL}${path}`
  const formData = new FormData()
  formData.append("file", file)
  const res = await requester.post(url, formData, fetchOptions)
  if (!res.ok) {
    let errMsg = "No se pudo subir el archivo"
    try {
      const errBody = await res.clone().json()
      if (typeof errBody?.error === "string" && errBody.error.trim()) {
        errMsg = errBody.error
      }
    } catch {
      // ignore
    }
    return { success: false, error: errMsg }
  }
  let body
  try {
    body = await res.json()
  } catch {
    return { success: false, error: "Respuesta inválida" }
  }
  const data = body?.data
  const outUrl = data?.url
  if (typeof outUrl !== "string" || !outUrl) {
    return { success: false, error: "Respuesta inválida" }
  }
  return {
    success: true,
    url: outUrl,
    storagePath:
      typeof data.storagePath === "string" ? data.storagePath : undefined,
  }
}
